/**
 * Shared voice constants (Wave 0).
 * Dependency-free: no React, DOM APIs, or network imports.
 * Keep in sync with server/services/voice/contracts.js and Section 5 of Voice_TODO_1.md.
 */

import type {
  CaptureStopReason,
  VoiceErrorCode,
  VoiceIdOwnership,
  VoiceIntentCategory,
  VoicePausePreset,
} from './types';

/** Capture timing defaults (milliseconds). */
export const VOICE_TIMING = {
  /** Max wait for first speech after mic opens. */
  INITIAL_SILENCE_MS: 8_000,
  /** Silence after speech before finalize (matches pause preset "normal"). */
  POST_SPEECH_SILENCE_MS: 2_500,
  /** Hard cap on a single capture. */
  MAX_RECORDING_MS: 45_000,
  /** Speech must last at least this long to count as speech-detected. */
  MIN_SUSTAINED_SPEECH_MS: 180,
  /** Ambient noise floor calibration window. */
  AMBIENT_CALIBRATION_MS_MIN: 300,
  AMBIENT_CALIBRATION_MS_MAX: 500,
  AMBIENT_CALIBRATION_MS_DEFAULT: 400,
} as const;

/** Post-speech silence pause presets (milliseconds). */
export const VOICE_PAUSE_PRESETS: Record<Exclude<VoicePausePreset, 'manual'>, number> & {
  manual: null;
} = {
  short: 1_500,
  normal: 2_500,
  long: 4_000,
  /** User must tap Done; silence does not auto-submit. */
  manual: null,
};

export const DEFAULT_VOICE_PAUSE_PRESET: VoicePausePreset = 'normal';

/**
 * Intent categories for confirmation / auth policy branching.
 * Do not list individual intents here — the permission map owns that.
 */
export const VOICE_INTENT_CATEGORIES: readonly VoiceIntentCategory[] = [
  'read_only',
  'navigation',
  'mutation',
  'confirmation',
  'meta',
] as const;

/**
 * Category semantics (policy hints only; RBAC is server-side).
 * - read_only: queries, lists, summaries — no confirmation required
 * - navigation: page / filter / view changes — no confirmation required
 * - mutation: create/update/delete/post financial or master data — confirmation required
 * - confirmation: confirm / deny of a pending mutation — continues pending_command_id
 * - meta: help, cancel, settings that do not mutate accounting records
 */
export const VOICE_INTENT_CATEGORY_POLICY = {
  read_only: { requiresConfirmation: false, isMutation: false },
  navigation: { requiresConfirmation: false, isMutation: false },
  mutation: { requiresConfirmation: true, isMutation: true },
  confirmation: { requiresConfirmation: false, isMutation: false },
  meta: { requiresConfirmation: false, isMutation: false },
} as const;

/** Stop reasons that may submit a capture exactly once. */
export const CAPTURE_SUBMIT_REASONS: readonly CaptureStopReason[] = [
  'silence',
  'user-done',
  'max-duration',
] as const;

/** Stop reasons that must discard capture (never submit). */
export const CAPTURE_DISCARD_REASONS: readonly CaptureStopReason[] = [
  'user-cancel',
  'navigation',
  'unmount',
  'logout',
  'business-switch',
  'superseded',
  'permission-cancelled',
  'recorder-error',
  'track-ended',
  'stream-inactive',
] as const;

/**
 * Client storage key suffixes under `voice:<userId>:<businessId>:`.
 * Build full keys with `buildVoiceStorageKey`.
 */
export const VOICE_STORAGE_SUFFIXES = {
  sessions: 'sessions',
  activeSession: 'active-session',
  backendSession: 'backend-session',
  pendingAction: 'pending-action',
  pendingDocument: 'pending-document',
  settings: 'settings',
} as const;

export type VoiceStorageSuffix =
  (typeof VOICE_STORAGE_SUFFIXES)[keyof typeof VOICE_STORAGE_SUFFIXES];

/** Pending operational client state TTL window (ms). */
export const VOICE_PENDING_TTL = {
  MIN_MS: 10 * 60 * 1000,
  MAX_MS: 30 * 60 * 1000,
  DEFAULT_MS: 15 * 60 * 1000,
} as const;

/**
 * Canonical error codes returned in VoiceCommandResponse.error_code
 * and/or HTTP error payloads for voice routes.
 */
export const VOICE_ERROR_CODES = {
  DUPLICATE_COMMAND: 'VOICE_DUPLICATE_COMMAND',
  FORBIDDEN: 'VOICE_FORBIDDEN',
  CONFIRMATION_EXPIRED: 'VOICE_CONFIRMATION_EXPIRED',
  STALE_ENTITY: 'VOICE_STALE_ENTITY',
  INVALID_SESSION: 'VOICE_INVALID_SESSION',
  MISSING_COMMAND_ID: 'VOICE_MISSING_COMMAND_ID',
  LEGACY_COMMAND_ID_ASSIGNED: 'VOICE_LEGACY_COMMAND_ID_ASSIGNED',
  RATE_LIMITED: 'VOICE_RATE_LIMITED',
  QUOTA_EXCEEDED: 'VOICE_QUOTA_EXCEEDED',
  INTERNAL_ERROR: 'VOICE_INTERNAL_ERROR',
} as const satisfies Record<string, VoiceErrorCode>;

/**
 * ID ownership (who generates each field).
 * - command_id: client generates once per logical command; server may assign during legacy window
 * - capture_id: client generates once when microphone capture starts
 * - ui_session_id: client; rotates on new chat, business switch, logout, conversation replace
 * - session_id: server-issued / server-accepted only after ownership validation
 * - pending_command_id: client echoes the original mutation command_id on confirmation
 */
export const VOICE_ID_OWNERSHIP: VoiceIdOwnership = {
  command_id: 'client',
  capture_id: 'client',
  ui_session_id: 'client',
  session_id: 'server',
  pending_command_id: 'client',
};

/**
 * Legacy clients missing command_id.
 * During this window the server may generate a command_id and warn
 * (VOICE_LEGACY_COMMAND_ID_ASSIGNED). Mutation execution must still be idempotent
 * via the durable ledger once an ID exists.
 */
export const VOICE_LEGACY_COMMAND_ID = {
  /** Calendar days after contracts ship during which server may mint missing IDs. */
  MIGRATION_WINDOW_DAYS: 30,
  /** Response warning code when server assigns command_id for a legacy request. */
  WARNING_CODE: VOICE_ERROR_CODES.LEGACY_COMMAND_ID_ASSIGNED,
  /**
   * After the window: reject mutation-capable requests missing command_id.
   * Read-only / navigation may still receive a server-minted ID with a warning
   * until hard cutover (coordinator decision).
   */
  REJECT_MUTATIONS_WITHOUT_ID_AFTER_WINDOW: true,
} as const;

export function buildVoiceStorageKey(
  userId: string,
  businessId: string,
  suffix: VoiceStorageSuffix
): string {
  return `voice:${userId}:${businessId}:${suffix}`;
}

export function isCaptureSubmitReason(reason: CaptureStopReason): boolean {
  return (CAPTURE_SUBMIT_REASONS as readonly string[]).includes(reason);
}

export function isCaptureDiscardReason(reason: CaptureStopReason): boolean {
  return (CAPTURE_DISCARD_REASONS as readonly string[]).includes(reason);
}
